import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem
} from '../ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ImageUpload } from '../ImageUpload';
import { uploadProductImage } from '../../services/imageService';
import { ProductPicker } from '../ProductPicker';
import { getActiveShippingTiers, ShippingTier } from '../../services/shippingService';
import { Bundle, BundleItem, BundlePriceMap, BundlePurchaseTypesConfig, SupportedCurrency, Product, BundleDefaultAddress } from '../../types';
import { toast } from 'sonner';
import {
  Package,
  DollarSign,
  Tag,
  ShoppingBag,
  Truck,
  Calendar,
  Save,
  X,
  Wand2,
  Sparkles,
  Image as ImageIcon,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Plus,
  Minus,
  Upload
} from 'lucide-react';

const SUPPORTED_CURRENCIES: SupportedCurrency[] = ['usd', 'cad', 'gbp'];
const PURCHASE_TYPE_LABELS: Record<keyof BundlePurchaseTypesConfig, string> = {
  oneTime: 'One-Time Purchase',
  subscription: 'Subscription'
};

const SHIPPING_NONE_VALUE = '__none__';

const defaultPrices: BundlePriceMap = {
  usd: { oneTime: null, subscription: null },
  cad: { oneTime: null, subscription: null },
  gbp: { oneTime: null, subscription: null }
};

const createEmptyDefaultAddress = (): BundleDefaultAddress => ({
  name: '',
  phone: '',
  email: '',
  address: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  }
});

export interface BundleFormValues {
  name: string;
  slug: string;
  description: string;
  images: string[];
  heroImage?: string;
  tags: string[];
  category?: string;
  displayOrder: number | null;
  purchaseTypes: BundlePurchaseTypesConfig;
  prices: BundlePriceMap;
  items: BundleItem[];
  shippingConfig: {
    oneTimeTierId?: string | null;
    subscriptionTierId?: string | null;
  };
  addressPolicy: 'fixed' | 'latest' | 'adminProvided';
  defaultAddress: BundleDefaultAddress;
  published: boolean;
  disabled: boolean;
  visibleFrom: Date | null;
  visibleTo: Date | null;
}

interface BundleFormSubmitData {
  values: BundleFormValues;
  originalImages: string[];
  imagesToDelete: string[];
}

interface BundleFormProps {
  open: boolean;
  mode: 'create' | 'edit';
  bundle?: Bundle | null;
  cloneSource?: Bundle | null;
  onClose: () => void;
  onSubmit: (data: BundleFormSubmitData) => Promise<void>;
}

const createDefaultValues = (): BundleFormValues => ({
  name: '',
  slug: '',
  description: '',
  images: [],
  heroImage: undefined,
  tags: [],
  category: undefined,
  displayOrder: null,
  purchaseTypes: { oneTime: true, subscription: false },
  prices: defaultPrices,
  items: [],
  shippingConfig: {
    oneTimeTierId: null,
    subscriptionTierId: null
  },
  addressPolicy: 'fixed',
  defaultAddress: createEmptyDefaultAddress(),
  published: false,
  disabled: false,
  visibleFrom: null,
  visibleTo: null
});

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const clonePrices = (prices: BundlePriceMap): BundlePriceMap => ({
  usd: { ...prices.usd },
  cad: { ...prices.cad },
  gbp: { ...prices.gbp }
});

const bundleToFormValues = (bundle: Bundle): BundleFormValues => ({
  name: bundle.name || '',
  slug: bundle.slug || slugify(bundle.name || ''),
  description: bundle.description || '',
  images: bundle.images || [],
  heroImage: bundle.heroImage || (bundle.images?.[0] ?? undefined),
  tags: bundle.tags || [],
  category: bundle.category,
  displayOrder: typeof bundle.displayOrder === 'number' ? bundle.displayOrder : null,
  purchaseTypes: {
    oneTime: bundle.purchaseTypes?.oneTime ?? false,
    subscription: bundle.purchaseTypes?.subscription ?? false
  },
  prices: clonePrices(bundle.prices || defaultPrices),
  items: (bundle.items || []).map((item) => ({
    productId: item.productId,
    quantity: item.quantity || 1,
    productName: item.productName,
    image: item.image,
    listingQuantity: item.listingQuantity,
    isCustom: item.isCustom ?? false,
    customSku: item.customSku ?? null,
    customDescription: item.customDescription ?? null,
    brand: item.brand ?? null,
    netWeight: item.netWeight ?? null,
    weightUnit: item.weightUnit ?? null
  })),
  shippingConfig: {
    oneTimeTierId: bundle.shippingConfig?.oneTimeTierId ?? null,
    subscriptionTierId: bundle.shippingConfig?.subscriptionTierId ?? null
  },
  addressPolicy: bundle.addressPolicy || 'fixed',
  defaultAddress: bundle.defaultAddress
    ? {
        name: bundle.defaultAddress.name || '',
        phone: bundle.defaultAddress.phone || '',
        email: bundle.defaultAddress.email || '',
        address: {
          line1: bundle.defaultAddress.address?.line1 || '',
          line2: bundle.defaultAddress.address?.line2 || '',
          city: bundle.defaultAddress.address?.city || '',
          state: bundle.defaultAddress.address?.state || '',
          postal_code: bundle.defaultAddress.address?.postal_code || '',
          country: bundle.defaultAddress.address?.country || ''
        }
      }
    : createEmptyDefaultAddress(),
  published: bundle.published ?? false,
  disabled: bundle.disabled ?? false,
  visibleFrom: bundle.visibleFrom ? new Date(bundle.visibleFrom) : null,
  visibleTo: bundle.visibleTo ? new Date(bundle.visibleTo) : null
});

export const BundleForm: React.FC<BundleFormProps> = ({
  open,
  mode,
  bundle,
  cloneSource = null,
  onClose,
  onSubmit
}) => {
  const [values, setValues] = useState<BundleFormValues>(createDefaultValues());
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [showCustomItemForm, setShowCustomItemForm] = useState(false);
  const [customItemForm, setCustomItemForm] = useState({
    name: '',
    brand: '',
    quantity: 1,
    listingQuantity: 1,
    netWeight: '',
    weightUnit: 'g' as 'g' | 'kg' | 'oz' | 'lb' | 'ml',
    image: ''
  });
  const [customImageUploading, setCustomImageUploading] = useState(false);
  const customImageInputRef = useRef<HTMLInputElement | null>(null);
  const [shippingTiers, setShippingTiers] = useState<ShippingTier[]>([]);
  const [loadingShippingTiers, setLoadingShippingTiers] = useState(false);
  const [originalImages, setOriginalImages] = useState<string[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (open) {
      setLoadingShippingTiers(true);
      getActiveShippingTiers()
        .then((tiers) => setShippingTiers(tiers))
        .catch(() => setShippingTiers([]))
        .finally(() => setLoadingShippingTiers(false));
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && bundle) {
      setValues(bundleToFormValues(bundle));
      setOriginalImages(bundle.images || []);
    } else if (mode === 'create' && cloneSource) {
      const clonedValues = bundleToFormValues(cloneSource);
      const baseSlug = clonedValues.slug || cloneSource.slug || slugify(cloneSource.name || '');
      clonedValues.slug = slugify(`${baseSlug || 'bundle'}-copy`);
      clonedValues.name = clonedValues.name ? `${clonedValues.name} (Copy)` : '';
      setValues(clonedValues);
      setOriginalImages([]);
    } else {
      setValues(createDefaultValues());
      setOriginalImages([]);
    }

    setImagesToDelete([]);
    setErrors([]);
    setNewTag('');
  }, [open, mode, bundle, cloneSource]);

  const handleInputChange = (key: keyof BundleFormValues, value: any) => {
    setValues((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleCustomItemFormChange = (field: keyof typeof customItemForm, value: string | number) => {
    setCustomItemForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDefaultAddressFieldChange = (
    field: keyof Omit<BundleDefaultAddress, 'address'>,
    value: string
  ) => {
    setValues((prev) => ({
      ...prev,
      defaultAddress: {
        ...prev.defaultAddress,
        [field]: value
      }
    }));
  };

  const handleDefaultAddressLocationChange = (
    field: keyof BundleDefaultAddress['address'],
    value: string
  ) => {
    setValues((prev) => ({
      ...prev,
      defaultAddress: {
        ...prev.defaultAddress,
        address: {
          ...prev.defaultAddress.address,
          [field]: value
        }
      }
    }));
  };

  const resetCustomItemFormState = () => {
    setCustomItemForm({
      name: '',
      brand: '',
      quantity: 1,
      image: '',
      listingQuantity: 1,
      netWeight: '',
      weightUnit: 'g'
    });
    setCustomImageUploading(false);
    if (customImageInputRef.current) {
      customImageInputRef.current.value = '';
    }
  };

  const handleCustomImageFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setCustomImageUploading(true);
      const url = await uploadProductImage(file);
      handleCustomItemFormChange('image', url);
      toast.success('Custom item image uploaded');
    } catch (error) {
      console.error('Failed to upload custom item image:', error);
      toast.error(error instanceof Error ? error.message : 'Image upload failed');
    } finally {
      setCustomImageUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handlePriceChange = (
    currency: SupportedCurrency,
    field: 'oneTime' | 'subscription',
    value: string
  ) => {
    const numericValue = value === '' ? null : Number(value);
    if (numericValue !== null && (Number.isNaN(numericValue) || numericValue < 0)) {
      return;
    }

    setValues((prev) => {
      const prices = clonePrices(prev.prices);
      prices[currency] = {
        ...prices[currency],
        [field]: numericValue
      };
      return {
        ...prev,
        prices
      };
    });
  };

  const handleTogglePurchaseType = (type: keyof BundlePurchaseTypesConfig, enabled: boolean) => {
    setValues((prev) => ({
      ...prev,
      purchaseTypes: {
        ...prev.purchaseTypes,
        [type]: enabled
      }
    }));
  };

  const handleAddTag = () => {
    const trimmed = newTag.trim();
    if (!trimmed) return;
    if (values.tags.includes(trimmed)) {
      toast.error('Tag already added');
      return;
    }
    handleInputChange('tags', [...values.tags, trimmed]);
    setNewTag('');
  };

  const handleRemoveTag = (tag: string) => {
    handleInputChange('tags', values.tags.filter((t) => t !== tag));
  };

  const handleAddProduct = (product: Product) => {
    setValues((prev) => {
      const existing = prev.items.find((item) => item.productId === product.id);
      if (existing) {
        return {
          ...prev,
          items: prev.items.map((item) =>
            item.productId === product.id
              ? { ...item, quantity: item.quantity + 1 }
              : item
          )
        };
      }

    const newItem: BundleItem = {
      productId: product.id,
      quantity: 1,
      productName: product.name,
      image: product.image || product.images?.[0] || undefined,
      listingQuantity: product.listingQuantity || 1,
      brand: product.brand || null,
      netWeight: product.netWeight ?? null,
      weightUnit: (product.weightUnit as BundleItem['weightUnit']) || null
    };

      return {
        ...prev,
        items: [...prev.items, newItem]
      };
    });
  };

  const handleItemQuantityChange = (productId: string, quantity: number) => {
    if (Number.isNaN(quantity) || quantity <= 0) return;
    setValues((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.productId === productId ? { ...item, quantity } : item
      )
    }));
  };

  const handleItemUpdate = (productId: string, updates: Partial<BundleItem>) => {
    setValues((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.productId === productId ? { ...item, ...updates } : item
      )
    }));
  };

  const handleRemoveItem = (productId: string) => {
    setValues((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.productId !== productId)
    }));
  };

  const handleAddCustomItem = () => {
    const name = customItemForm.name.trim();
    const brand = customItemForm.brand.trim();
    const quantity = Number(customItemForm.quantity);
    const listingQuantity = Number(customItemForm.listingQuantity) || 1;
    const netWeightInput = typeof customItemForm.netWeight === 'string'
      ? customItemForm.netWeight.trim()
      : String(customItemForm.netWeight ?? '');
    const hasNetWeight = netWeightInput !== '';
    const netWeight = hasNetWeight ? Number(netWeightInput) : undefined;

    if (!name) {
      toast.error('Custom item name is required');
      return;
    }

    if (Number.isNaN(quantity) || quantity <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }

    if (Number.isNaN(listingQuantity) || listingQuantity <= 0) {
      toast.error('Units per listing must be greater than 0');
      return;
    }

    if (hasNetWeight && (Number.isNaN(netWeight) || (netWeight ?? 0) <= 0)) {
      toast.error('Custom item weight must be greater than 0 when provided');
      return;
    }

    if (!customItemForm.image.trim()) {
      toast.error('Custom item image is required');
      return;
    }

    const customId = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const newItem: BundleItem = {
      productId: customId,
      productName: name,
      brand: brand || undefined,
      quantity,
      image: customItemForm.image?.trim() || undefined,
      listingQuantity: listingQuantity > 0 ? listingQuantity : 1,
      isCustom: true,
      netWeight: typeof netWeight === 'number' ? netWeight : undefined,
      weightUnit: typeof netWeight === 'number' ? customItemForm.weightUnit : undefined,
      customSku: undefined
    };

    setValues((prev) => ({
      ...prev,
      items: [...prev.items, newItem]
    }));

    resetCustomItemFormState();
    setShowCustomItemForm(false);
    toast.success('Custom item added to bundle');
  };

  const formTitle = mode === 'create' ? 'Create Bundle' : `Edit ${bundle?.name ?? 'Bundle'}`;

  const purchaseTypeSummary = useMemo(() => {
    const summary: string[] = [];
    if (values.purchaseTypes.oneTime) summary.push('One-Time');
    if (values.purchaseTypes.subscription) summary.push('Subscription');
    return summary.length > 0 ? summary.join(' & ') : 'No purchase type selected';
  }, [values.purchaseTypes]);

  const validateForm = (): string[] => {
    const validationErrors: string[] = [];

    if (!values.name.trim()) {
      validationErrors.push('Name is required');
    }

    if (!values.slug.trim()) {
      validationErrors.push('Slug is required');
    }

    if (values.displayOrder != null) {
      if (!Number.isInteger(values.displayOrder)) {
        validationErrors.push('Display order must be a whole number');
      } else if (values.displayOrder < 1) {
        validationErrors.push('Display order must be at least 1');
      }
    }

    if (!values.purchaseTypes.oneTime && !values.purchaseTypes.subscription) {
      validationErrors.push('At least one purchase type must be enabled');
    }

    SUPPORTED_CURRENCIES.forEach((currency) => {
      if (values.purchaseTypes.oneTime && (values.prices[currency].oneTime == null || values.prices[currency].oneTime === 0)) {
        validationErrors.push(`${currency.toUpperCase()} one-time price is required and must be greater than 0`);
      }
      if (values.purchaseTypes.subscription && (values.prices[currency].subscription == null || values.prices[currency].subscription === 0)) {
        validationErrors.push(`${currency.toUpperCase()} subscription price is required and must be greater than 0`);
      }
    });

    if (values.items.length === 0) {
      validationErrors.push('Add at least one product to the bundle');
    }

    if (values.purchaseTypes.oneTime && !values.shippingConfig.oneTimeTierId) {
      validationErrors.push('Select a shipping tier for one-time purchases');
    }

    if (values.purchaseTypes.subscription && !values.shippingConfig.subscriptionTierId) {
      validationErrors.push('Select a shipping tier for subscriptions');
    }

    if (values.addressPolicy === 'adminProvided') {
      const addr = values.defaultAddress;
      if (!addr.name.trim()) {
        validationErrors.push('Default contact name is required for admin-provided addresses');
      }
      if (!addr.address.line1.trim()) {
        validationErrors.push('Default address line 1 is required');
      }
      if (!addr.address.city.trim()) {
        validationErrors.push('Default city is required');
      }
      if (!addr.address.state.trim()) {
        validationErrors.push('Default state or parish is required');
      }
      if (!addr.address.postal_code.trim()) {
        validationErrors.push('Default postal code is required');
      }
      if (!addr.address.country.trim()) {
        validationErrors.push('Default country is required');
      }
    }

    values.items.forEach((item, index) => {
      if (!item.quantity || item.quantity <= 0) {
        validationErrors.push(`Item "${item.productName || item.productId}" must have a quantity of at least 1`);
      }
      if (item.isCustom) {
        const label = item.productName || `Custom Item #${index + 1}`;
        if (!item.productName?.trim()) {
          validationErrors.push(`${label} must have a name`);
        }
        if (!item.image?.trim()) {
          validationErrors.push(`${label} requires an image`);
        }
        if (!item.listingQuantity || item.listingQuantity <= 0) {
          validationErrors.push(`${label} must have units per listing greater than 0`);
        }
        if (typeof item.netWeight === 'number') {
          if (item.netWeight <= 0) {
            validationErrors.push(`${label} must have a weight greater than 0 when provided`);
          }
          if (!item.weightUnit) {
            validationErrors.push(`${label} must have a weight unit selected when providing a weight`);
          }
        }
      }
    });

    return validationErrors;
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      toast.error('Please resolve the highlighted issues before saving');
      return;
    }

    setErrors([]);
    setSaving(true);

    try {
      await onSubmit({
        values,
        originalImages,
        imagesToDelete
      });
      toast.success(`Bundle ${mode === 'create' ? 'created' : 'updated'} successfully`);
      onClose();
    } catch (error) {
      console.error('Failed to save bundle:', error);
      const message = error instanceof Error ? error.message : 'Failed to save bundle';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSlug = () => {
    if (!values.name.trim()) {
      toast.error('Please enter a bundle name first');
      return;
    }
    const generated = slugify(values.name);
    handleInputChange('slug', generated);
  };

  const handleDisplayOrderInput = (rawValue: string) => {
    if (rawValue === '') {
      handleInputChange('displayOrder', null);
      return;
    }
    const parsed = Number(rawValue);
    if (Number.isNaN(parsed)) {
      return;
    }
    handleInputChange('displayOrder', parsed);
  };

  const renderCurrencyPricing = (currency: SupportedCurrency) => {
    const currencyLabel = currency.toUpperCase();
    return (
      <Card key={currency} className="shadow-sm border-gray-200">
        <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-t-2xl">
          <CardTitle className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-purple-600" />
            {currencyLabel}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wide text-gray-500">One-Time Price</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={values.prices[currency].oneTime ?? ''}
              onChange={(e) => handlePriceChange(currency, 'oneTime', e.target.value)}
              placeholder="0.00"
              disabled={!values.purchaseTypes.oneTime}
              className="mt-2 h-11 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-gray-500">Subscription Price (Monthly)</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={values.prices[currency].subscription ?? ''}
              onChange={(e) => handlePriceChange(currency, 'subscription', e.target.value)}
              placeholder="0.00"
              disabled={!values.purchaseTypes.subscription}
              className="mt-2 h-11 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
            />
          </div>
        </CardContent>
      </Card>
    );
  };

  const handleShippingTierSelect = (
    field: 'oneTimeTierId' | 'subscriptionTierId',
    tierId: string
  ) => {
    handleInputChange('shippingConfig', {
      ...values.shippingConfig,
      [field]: tierId === SHIPPING_NONE_VALUE ? null : tierId
    });
  };

  const formatDateInput = (date: Date | null): string => {
    if (!date) return '';
    const localDate = new Date(date);
    const offset = localDate.getTimezoneOffset();
    localDate.setMinutes(localDate.getMinutes() - offset);
    return localDate.toISOString().split('T')[0];
  };

  const handleVisibilityDateChange = (
    field: 'visibleFrom' | 'visibleTo',
    value: string
  ) => {
    handleInputChange(field, value ? new Date(`${value}T00:00:00`) : null);
  };

  const totalItemsSelected = values.items.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const hasSubscription = values.purchaseTypes.subscription;
  const hasOneTime = values.purchaseTypes.oneTime;

  return (
    <>
      <AnimatePresence>
        {open && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
            style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: 'spring',
                duration: 0.5,
                bounce: 0.3
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
            >
              <div className="relative bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-transparent" />
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', bounce: 0.6 }}
                      className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <Package className="h-6 w-6 text-white" />
                    </motion.div>
                    <div className="space-y-2">
                      <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-2xl font-bold text-white"
                      >
                        {mode === 'create' ? 'Create Bundle' : 'Edit Bundle'}
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/80 text-sm"
                      >
                        {mode === 'create'
                          ? "Launch a curated JamPack'd bundle experience"
                          : `Update ${bundle?.name || 'bundle'} details`}
                      </motion.p>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                        className="text-white/70 text-xs"
                      >
                        {purchaseTypeSummary}
                      </motion.p>
                    </div>
                  </div>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClose}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-b from-gray-50/50 to-white">
                <form id="bundle-form" ref={formRef} onSubmit={handleSubmit} className="space-y-8">
                  {errors.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-50 border border-red-200 rounded-xl p-4"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-red-600" />
                        <h4 className="font-medium text-red-800">Please fix the following issues:</h4>
                      </div>
                      <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                        {errors.map((error) => (
                          <li key={error}>{error}</li>
                        ))}
                      </ul>
                    </motion.div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                      <Card className="shadow-sm border-gray-200 h-full">
                        <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/60 rounded-t-2xl">
                          <CardTitle className="flex items-center gap-2 text-base text-gray-900">
                            <Package className="h-4 text-purple-600" />
                            Bundle Details
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6 space-y-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-3">
                              Bundle Name *
                            </label>
                            <Input
                              value={values.name}
                              onChange={(e) => handleInputChange('name', e.target.value)}
                              placeholder="JamPack'd Morning Starter"
                              className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-3">
                              Slug
                            </label>
                            <div className="flex gap-2">
                              <Input
                                value={values.slug}
                                onChange={(e) => handleInputChange('slug', slugify(e.target.value))}
                                placeholder="jampackd-morning-starter"
                                className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleGenerateSlug}
                                className="h-12 px-4 rounded-xl border-purple-200 hover:bg-purple-600 hover:text-white transition-all duration-300"
                              >
                                Generate
                              </Button>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-3">
                              Description
                            </label>
                            <Textarea
                              value={values.description}
                              onChange={(e) => handleInputChange('description', e.target.value)}
                              rows={5}
                              placeholder="Describe the bundle contents and value propositions"
                              className="rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200 resize-none"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-3">
                              Category
                            </label>
                            <Input
                              value={values.category ?? ''}
                              onChange={(e) => handleInputChange('category', e.target.value)}
                              placeholder="e.g., Holiday Limited Edition"
                              className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-900 mb-3">
                              Display Order
                            </label>
                            <Input
                              type="number"
                              min={1}
                              step={1}
                              value={values.displayOrder ?? ''}
                              onChange={(e) => handleDisplayOrderInput(e.target.value)}
                              placeholder="1"
                              className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                            />
                            <p className="mt-2 text-xs text-gray-500">
                              Lower numbers appear first on the customer site. Leave blank to use the default release order.
                            </p>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-3 text-sm font-medium text-gray-900">
                              <Tag className="h-4 w-4 text-purple-600" />
                              Tags
                            </div>
                            <div className="flex gap-2">
                              <Input
                                value={newTag}
                                onChange={(e) => setNewTag(e.target.value)}
                                placeholder="Add tag and press enter"
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddTag();
                                  }
                                }}
                                className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                onClick={handleAddTag}
                                disabled={!newTag.trim()}
                                className="h-12 px-4 rounded-xl border-purple-200 hover:bg-purple-600 hover:text-white transition-all duration-300"
                              >
                                Add
                              </Button>
                            </div>
                            {values.tags.length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                {values.tags.map((tag) => (
                                  <Badge
                                    key={tag}
                                    variant="outline"
                                    className="px-3 py-1 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 transition-all duration-200"
                                  >
                                    {tag}
                                    <button type="button" className="ml-2" onClick={() => handleRemoveTag(tag)}>
                                      <X className="h-3 w-3" />
                                    </button>
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                      <Card className="shadow-sm border-gray-200">
                        <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/60 rounded-t-2xl">
                          <CardTitle className="flex items-center gap-2 text-base text-gray-900">
                            <ImageIcon className="h-4 w-4 text-purple-600" />
                            Bundle Imagery
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                          <ImageUpload
                            existingImages={values.images}
                            onImagesChange={(images) => handleInputChange('images', images)}
                            onPrimaryImageChange={(primary) => handleInputChange('heroImage', primary)}
                            primaryImage={values.heroImage}
                            maxImages={8}
                            onImageMarkedForDeletion={(url) => {
                              if (!url.includes('firebasestorage.googleapis.com')) return;
                              setImagesToDelete((prev) => (prev.includes(url) ? prev : [...prev, url]));
                            }}
                          />
                        </CardContent>
                      </Card>
                    </motion.div>
                  </div>

                  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                    <Card className="shadow-sm border-gray-200">
                      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/60 rounded-t-2xl">
                        <CardTitle className="flex items-center gap-2 text-base text-gray-900">
                          <Sparkles className="h-4 w-4 text-purple-600" />
                          Offering Types & Pricing
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {(Object.keys(values.purchaseTypes) as Array<keyof BundlePurchaseTypesConfig>).map((typeKey) => (
                            <div
                              key={typeKey}
                              className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                                values.purchaseTypes[typeKey]
                                  ? 'border-purple-300 bg-purple-50 shadow-sm'
                                  : 'border-gray-200 bg-white hover:border-purple-200'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-sm font-semibold text-gray-900">{PURCHASE_TYPE_LABELS[typeKey]}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {typeKey === 'oneTime'
                                      ? 'Single purchase billed immediately'
                                      : 'Recurring monthly subscription'}
                                  </p>
                                </div>
                                <Switch
                                  checked={values.purchaseTypes[typeKey]}
                                  onCheckedChange={(checked) => handleTogglePurchaseType(typeKey, checked)}
                                  className="data-[state=checked]:bg-purple-600"
                                />
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {SUPPORTED_CURRENCIES.map(renderCurrencyPricing)}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
                    <Card className="shadow-sm border-gray-200">
                      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/60 rounded-t-2xl">
                        <CardTitle className="flex items-center gap-2 text-base text-gray-900">
                          <ShoppingBag className="h-4 w-4 text-purple-600" />
                          Bundle Composition
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6 space-y-6">
                        <div className="flex flex-col gap-3">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">Selected Products</p>
                              <p className="text-xs text-gray-500">
                                {values.items.length === 0
                                  ? 'No products added yet'
                                  : `${values.items.length} product${values.items.length === 1 ? '' : 's'} • ${
                                      totalItemsSelected
                                    } total items per bundle`}
                              </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                              <Button
                                type="button"
                                onClick={() => setProductPickerOpen(true)}
                                className="h-11 px-5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-sm hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                              >
                                <Sparkles className="h-4 w-4" />
                                Add Products
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                  setShowCustomItemForm((prev) => {
                                    if (prev) {
                                      resetCustomItemFormState();
                                    }
                                    return !prev;
                                  })
                                }
                                className="h-11 px-5 rounded-xl border-purple-200 text-purple-700 hover:bg-purple-600 hover:text-white transition-all duration-300 flex items-center gap-2"
                              >
                                <Plus className="h-4 w-4" />
                                {showCustomItemForm ? 'Cancel Custom Item' : 'Add Custom Item'}
                              </Button>
                            </div>
                          </div>
                          {showCustomItemForm && (
                            <div className="rounded-2xl border border-purple-200 bg-purple-50/40 p-5 space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="space-y-1">
                                  <label className="text-xs font-medium text-gray-700">Item Name *</label>
                                  <Input
                                    value={customItemForm.name}
                                    onChange={(e) => handleCustomItemFormChange('name', e.target.value)}
                                    placeholder="e.g. Surprise Treat Pack"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-xs font-medium text-gray-700">Brand (optional)</label>
                                  <Input
                                    value={customItemForm.brand}
                                    onChange={(e) => handleCustomItemFormChange('brand', e.target.value)}
                                    placeholder="JamPack'd"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-xs font-medium text-gray-700">Quantity per bundle *</label>
                                  <Input
                                    type="number"
                                    min={1}
                                    value={customItemForm.quantity}
                                    onChange={(e) => handleCustomItemFormChange('quantity', Number(e.target.value))}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="text-xs font-medium text-gray-700">Units per listing *</label>
                                  <Input
                                    type="number"
                                    min={1}
                                    value={customItemForm.listingQuantity}
                                    onChange={(e) => handleCustomItemFormChange('listingQuantity', Number(e.target.value))}
                                  />
                                </div>
                                <div className="space-y-1 md:col-span-2">
                                  <label className="text-xs font-medium text-gray-700">Item Weight (optional)</label>
                                  <div className="flex gap-2">
                                    <Input
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      value={customItemForm.netWeight}
                                      onChange={(e) => handleCustomItemFormChange('netWeight', e.target.value)}
                                      placeholder="0.00"
                                    />
                                    <select
                                      value={customItemForm.weightUnit}
                                      onChange={(e) =>
                                        handleCustomItemFormChange('weightUnit', e.target.value as 'g' | 'kg' | 'oz' | 'lb' | 'ml')
                                      }
                                      className="h-12 px-3 rounded-xl border border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200 bg-white"
                                    >
                                      <option value="g">g</option>
                                      <option value="kg">kg</option>
                                      <option value="oz">oz</option>
                                      <option value="lb">lb</option>
                                      <option value="ml">ml</option>
                                    </select>
                                  </div>
                                </div>
                                <div className="space-y-1 md:col-span-2">
                                  <label className="text-xs font-medium text-gray-700">Item Image *</label>
                                  <div className="space-y-2">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                                      <Input
                                        value={customItemForm.image}
                                        onChange={(e) => handleCustomItemFormChange('image', e.target.value)}
                                        placeholder="https://..."
                                      />
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        ref={customImageInputRef}
                                        onChange={handleCustomImageFileChange}
                                      />
                                      <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => customImageInputRef.current?.click()}
                                        disabled={customImageUploading}
                                        className="flex items-center gap-2"
                                      >
                                        {customImageUploading ? (
                                          <>
                                            <Loader2 className="h-4 w-4 animate-spin" /> Uploading...
                                          </>
                                        ) : (
                                          <>
                                            <Upload className="h-4 w-4" /> Upload Image
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                    {customItemForm.image && (
                                      <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
                                        <img
                                          src={customItemForm.image}
                                          alt="Custom item preview"
                                          className="h-16 w-16 rounded-lg object-cover border border-gray-100"
                                        />
                                        <span className="text-xs text-gray-500 break-all">
                                          Using image: {customItemForm.image}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <Button type="button" onClick={handleAddCustomItem} className="h-11 px-5 rounded-xl">
                                  Save Custom Item
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => {
                                    setShowCustomItemForm(false);
                                    resetCustomItemFormState();
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        {values.items.length === 0 ? (
                          <div className="rounded-2xl border-2 border-dashed border-purple-200 bg-purple-50/40 p-8 text-center space-y-3">
                            <p className="text-sm font-semibold text-purple-700">Build your bundle</p>
                            <p className="text-xs text-purple-600">
                              Select products from your catalog to craft a standout JamPack'd experience.
                            </p>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setProductPickerOpen(true)}
                              className="h-11 px-5 rounded-xl border-purple-300 text-purple-700 hover:bg-purple-600 hover:text-white transition-all duration-300"
                            >
                              Choose Products
                            </Button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {values.items.map((item) => (
                              <div
                                key={item.productId}
                                className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border border-gray-200 rounded-xl p-4 bg-white shadow-sm"
                              >
                                <div className="flex items-start gap-4 w-full">
                                  <div className="flex flex-col items-center gap-2">
                                    {item.image ? (
                                      <img
                                        src={item.image}
                                        alt={item.productName}
                                        className="h-16 w-16 rounded-xl object-cover border border-gray-200"
                                      />
                                    ) : (
                                      <div className="h-16 w-16 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center">
                                        <Package className="h-6 w-6" />
                                      </div>
                                    )}
                                    {item.isCustom && (
                                      <Input
                                        placeholder="Image URL"
                                        value={item.image || ''}
                                        onChange={(e) => handleItemUpdate(item.productId, { image: e.target.value })}
                                        className="text-xs"
                                      />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    {item.isCustom ? (
                                      <div className="space-y-3 w-full">
                                        <div className="flex items-center gap-2">
                                          <p className="text-xs uppercase tracking-wide text-purple-600 font-semibold">
                                            Custom Item
                                          </p>
                                          <Badge variant="outline" className="border-purple-200 text-purple-700">
                                            Custom
                                          </Badge>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                          <Input
                                            value={item.productName || ''}
                                            onChange={(e) => handleItemUpdate(item.productId, { productName: e.target.value })}
                                            placeholder="Custom item name"
                                          />
                                          <Input
                                            value={item.brand || ''}
                                            onChange={(e) => handleItemUpdate(item.productId, { brand: e.target.value })}
                                            placeholder="Brand"
                                          />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                          <Input
                                            type="number"
                                            min={1}
                                            placeholder="Units per listing"
                                            value={item.listingQuantity || 1}
                                            onChange={(e) =>
                                              handleItemUpdate(item.productId, { listingQuantity: Number(e.target.value) || 1 })
                                            }
                                          />
                                          <div className="flex gap-2">
                                            <Input
                                              type="number"
                                              min={0}
                                              step="0.01"
                                              placeholder="Weight"
                                              value={item.netWeight ?? ''}
                                              onChange={(e) =>
                                                handleItemUpdate(item.productId, {
                                                  netWeight: e.target.value ? Number(e.target.value) : null
                                                })
                                              }
                                            />
                                            <select
                                              value={item.weightUnit || 'g'}
                                              onChange={(e) =>
                                                handleItemUpdate(item.productId, {
                                                  weightUnit: e.target.value as BundleItem['weightUnit']
                                                })
                                              }
                                              className="h-10 px-3 rounded-lg border border-gray-200 text-sm"
                                            >
                                              <option value="g">g</option>
                                              <option value="kg">kg</option>
                                              <option value="oz">oz</option>
                                              <option value="lb">lb</option>
                                              <option value="ml">ml</option>
                                            </select>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <p className="font-semibold text-gray-900">{item.productName}</p>
                                        {item.listingQuantity && item.listingQuantity > 1 && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            {item.listingQuantity} units per listing
                                          </p>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                  <div>
                                    <label className="text-xs uppercase tracking-wide text-gray-500">Quantity per bundle</label>
                                    <div className="mt-2 flex items-center gap-2">
                                      <Button
                                        type="button"
                                        size="icon"
                                        variant="outline"
                                        onClick={() => handleItemQuantityChange(item.productId, item.quantity - 1)}
                                        className="h-10 w-10 rounded-xl border-purple-200 text-purple-600 hover:bg-purple-50"
                                      >
                                        <Minus className="h-4 w-4" />
                                      </Button>
                                      <Input
                                        type="number"
                                        min={1}
                                        value={item.quantity}
                                        onChange={(e) => {
                                          const nextValue = Number(e.target.value);
                                          if (!Number.isNaN(nextValue)) {
                                            handleItemQuantityChange(item.productId, nextValue);
                                          }
                                        }}
                                        className="w-20 h-11 text-center rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20"
                                      />
                                      <Button
                                        type="button"
                                        size="icon"
                                        variant="outline"
                                        onClick={() => handleItemQuantityChange(item.productId, item.quantity + 1)}
                                        className="h-10 w-10 rounded-xl border-purple-200 text-purple-600 hover:bg-purple-50"
                                      >
                                        <Plus className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => handleRemoveItem(item.productId)}
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-11 rounded-xl"
                                  >
                                    <X className="h-4 w-4 mr-2" />
                                    Remove
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                    <Card className="shadow-sm border-gray-200">
                      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/60 rounded-t-2xl">
                        <CardTitle className="flex items-center gap-2 text-base text-gray-900">
                          <Truck className="h-4 w-4 text-purple-600" />
                          Shipping & Fulfillment
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6 space-y-6">
                        {loadingShippingTiers ? (
                          <div className="flex items-center gap-3 text-sm text-gray-600">
                            <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                            Loading shipping tiers...
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-3">
                              <label className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                <Truck className="h-4 w-4 text-purple-600" />
                                One-Time Shipping Tier
                              </label>
                              <Select
                                value={values.shippingConfig.oneTimeTierId ?? SHIPPING_NONE_VALUE}
                                onValueChange={(value) => handleShippingTierSelect('oneTimeTierId', value)}
                                disabled={!hasOneTime}
                              >
                                <SelectTrigger
                                  className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
                                >
                                  <SelectValue placeholder="Select shipping tier" />
                                </SelectTrigger>
                                <SelectContent className="z-[10010] bg-white border border-purple-100 shadow-lg">
                                  <SelectItem value={SHIPPING_NONE_VALUE} disabled>
                                    Select shipping tier
                                  </SelectItem>
                                  {shippingTiers.map((tier) => (
                                    <SelectItem key={tier.id} value={tier.id}>
                                      <div className="flex flex-col">
                                        <span className="font-medium text-gray-900">{tier.name}</span>
                                        {tier.description && (
                                          <span className="text-xs text-gray-500">{tier.description}</span>
                                        )}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {!hasOneTime && (
                                <p className="text-xs text-gray-500">
                                  Enable one-time purchases to configure one-time shipping.
                                </p>
                              )}
                            </div>

                            <div className="space-y-3">
                              <label className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                <Truck className="h-4 w-4 text-purple-600" />
                                Subscription Shipping Tier
                              </label>
                              <Select
                                value={values.shippingConfig.subscriptionTierId ?? SHIPPING_NONE_VALUE}
                                onValueChange={(value) => handleShippingTierSelect('subscriptionTierId', value)}
                                disabled={!hasSubscription}
                              >
                                <SelectTrigger
                                  className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50"
                                >
                                  <SelectValue placeholder="Select shipping tier" />
                                </SelectTrigger>
                                <SelectContent className="z-[10010] bg-white border border-purple-100 shadow-lg">
                                  <SelectItem value={SHIPPING_NONE_VALUE} disabled>
                                    Select shipping tier
                                  </SelectItem>
                                  {shippingTiers.map((tier) => (
                                    <SelectItem key={tier.id} value={tier.id}>
                                      <div className="flex flex-col">
                                        <span className="font-medium text-gray-900">{tier.name}</span>
                                        {tier.description && (
                                          <span className="text-xs text-gray-500">{tier.description}</span>
                                        )}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {!hasSubscription && (
                                <p className="text-xs text-gray-500">
                                  Enable subscriptions above to configure recurring shipping.
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <label className="text-sm font-medium text-gray-900 flex items-center gap-2">
                              <Wand2 className="h-4 w-4 text-purple-600" />
                              Address Policy
                            </label>
                            <Select
                              value={values.addressPolicy}
                              onValueChange={(value) => handleInputChange('addressPolicy', value as 'fixed' | 'latest' | 'adminProvided')}
                            >
                              <SelectTrigger className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md transition-all duration-200">
                                <SelectValue placeholder="Choose address policy" />
                              </SelectTrigger>
                              <SelectContent className="z-[10010] bg-white border border-purple-100 shadow-lg">
                                <SelectItem value="fixed">
                                  Use checkout shipping address unless customer updates
                                </SelectItem>
                                <SelectItem value="latest">
                                  Always use the most recent shipping address on file
                                </SelectItem>
                                <SelectItem value="adminProvided">
                                  Use the preset address configured below for every purchase
                                </SelectItem>
                              </SelectContent>
                            </Select>
                            {values.addressPolicy === 'adminProvided' && (
                              <div className="mt-4 rounded-2xl border border-purple-100 bg-purple-50/40 p-4 space-y-4">
                                <div>
                                  <p className="text-sm font-semibold text-purple-800">Default Fulfillment Address</p>
                                  <p className="text-xs text-purple-700 mt-1">
                                    Customers will not be asked for an address. Orders will ship to this destination automatically.
                                  </p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Contact Name</label>
                                    <Input
                                      value={values.defaultAddress.name}
                                      onChange={(e) => handleDefaultAddressFieldChange('name', e.target.value)}
                                      placeholder="JamPack'd Warehouse"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Contact Email</label>
                                    <Input
                                      value={values.defaultAddress.email}
                                      onChange={(e) => handleDefaultAddressFieldChange('email', e.target.value)}
                                      placeholder="warehouse@jampackd.com"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Phone Number</label>
                                    <Input
                                      value={values.defaultAddress.phone}
                                      onChange={(e) => handleDefaultAddressFieldChange('phone', e.target.value)}
                                      placeholder="(876) 000-0000"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Address Line 1</label>
                                    <Input
                                      value={values.defaultAddress.address.line1}
                                      onChange={(e) => handleDefaultAddressLocationChange('line1', e.target.value)}
                                      placeholder="123 Harbour St"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Address Line 2</label>
                                    <Input
                                      value={values.defaultAddress.address.line2}
                                      onChange={(e) => handleDefaultAddressLocationChange('line2', e.target.value)}
                                      placeholder="Suite 4"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">City</label>
                                    <Input
                                      value={values.defaultAddress.address.city}
                                      onChange={(e) => handleDefaultAddressLocationChange('city', e.target.value)}
                                      placeholder="Kingston"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">State / Parish</label>
                                    <Input
                                      value={values.defaultAddress.address.state}
                                      onChange={(e) => handleDefaultAddressLocationChange('state', e.target.value)}
                                      placeholder="St. Andrew"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Postal Code</label>
                                    <Input
                                      value={values.defaultAddress.address.postal_code}
                                      onChange={(e) => handleDefaultAddressLocationChange('postal_code', e.target.value)}
                                      placeholder="00000"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-xs font-medium text-gray-700">Country</label>
                                    <Input
                                      value={values.defaultAddress.address.country}
                                      onChange={(e) => handleDefaultAddressLocationChange('country', e.target.value)}
                                      placeholder="Jamaica"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
                    <Card className="shadow-sm border-gray-200">
                      <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100/60 rounded-t-2xl">
                        <CardTitle className="flex items-center gap-2 text-base text-gray-900">
                          <Calendar className="h-4 w-4 text-purple-600" />
                          Visibility & Status
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-3">
                            <label className="text-sm font-medium text-gray-900">Visible From</label>
                            <Input
                              type="date"
                              value={formatDateInput(values.visibleFrom)}
                              onChange={(e) => handleVisibilityDateChange('visibleFrom', e.target.value)}
                              className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md transition-all duration-200"
                            />
                          </div>
                          <div className="space-y-3">
                            <label className="text-sm font-medium text-gray-900">Visible Until</label>
                            <Input
                              type="date"
                              value={formatDateInput(values.visibleTo)}
                              onChange={(e) => handleVisibilityDateChange('visibleTo', e.target.value)}
                              className="h-12 rounded-xl border-gray-200 focus:border-purple-600 focus:ring-purple-600/20 shadow-sm hover:shadow-md transition-all duration-200"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="flex items-center justify-between rounded-xl border border-green-100 bg-green-50/60 p-4">
                            <div>
                              <p className="text-sm font-semibold text-green-700 flex items-center gap-2">
                                <CheckCircle2 className="h-4 w-4" />
                                Published
                              </p>
                              <p className="text-xs text-green-600 mt-1">
                                Published bundles appear on the customer storefront.
                              </p>
                            </div>
                            <Switch
                              checked={values.published}
                              onCheckedChange={(checked) => handleInputChange('published', checked)}
                              className="data-[state=checked]:bg-green-500"
                            />
                          </div>
                          <div className="flex items-center justify-between rounded-xl border border-amber-100 bg-amber-50/60 p-4">
                            <div>
                              <p className="text-sm font-semibold text-amber-700 flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4" />
                                Disable for maintenance
                              </p>
                              <p className="text-xs text-amber-600 mt-1">
                                Temporarily hide this bundle while keeping historical data intact.
                              </p>
                            </div>
                            <Switch
                              checked={values.disabled}
                              onCheckedChange={(checked) => handleInputChange('disabled', checked)}
                              className="data-[state=checked]:bg-amber-500"
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleClose}
                      disabled={saving}
                      className="h-12 px-6 rounded-xl border-purple-200 hover:bg-purple-50 transition-all duration-200"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={saving}
                      className="h-12 px-6 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-sm hover:shadow-lg transition-all duration-300 flex items-center gap-2"
                    >
                      {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                      {saving ? 'Saving Bundle...' : mode === 'create' ? 'Create Bundle' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <ProductPicker
        isOpen={productPickerOpen}
        onClose={() => setProductPickerOpen(false)}
        onSelect={(product) => handleAddProduct(product)}
        excludeProductIds={values.items.map((item) => item.productId)}
      />
    </>
  );
};
